import React, { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { QRCodeCanvas } from "qrcode.react";
import { Moon, Sun, Copy, Check, Share2, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fileUrl } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export function Brand({ className = "", onClick }) {
  return (
    <div className={`flex items-center gap-2 cursor-pointer select-none ${className}`} onClick={onClick} data-testid="brand-logo">
      <img src="/coachi-logo.png" alt="Coachi" className="h-10 w-10 shrink-0 object-contain" />
      <span className="font-heading text-lg font-extrabold tracking-tight">Coachi</span>
    </div>
  );
}

export function ThemeToggle() {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return null;
  return (
    <Button
      variant="ghost" size="icon" data-testid="theme-toggle"
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      aria-label="Toggle theme"
    >
      {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
    </Button>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: "bg-muted text-muted-foreground",
    active: "bg-primary/15 text-primary",
    completed: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    archived: "bg-muted text-muted-foreground line-through",
  };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] || map.draft}`} data-testid="status-badge">
      {status}
    </span>
  );
}

export function LiveTag() {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-2.5 py-0.5 text-xs font-bold text-accent">
      <span className="live-dot" /> LIVE
    </span>
  );
}

export function CopyButton({ value, label = "Copy", testId }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      variant="outline" size="sm" data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
      {label}
    </Button>
  );
}

export function ShareDialog({ open, onOpenChange, title, url, code, subtitle }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="share-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{title}</DialogTitle>
        </DialogHeader>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
        <div className="flex flex-col items-center gap-4 py-2">
          <div className="rounded-xl bg-white p-4">
            <QRCodeCanvas value={url} size={180} />
          </div>
          {code && (
            <div className="text-center">
              <div className="text-label text-muted-foreground">Code</div>
              <div className="font-mono text-lg font-bold">{code}</div>
            </div>
          )}
          <div className="flex w-full flex-col gap-2">
            <div className="truncate rounded-md border bg-muted/40 px-3 py-2 text-center text-xs">{url}</div>
            <div className="flex gap-2">
              <CopyButton value={url} label="Copy link" testId="share-copy-link" />
              <Button
                variant="secondary" size="sm" className="flex-1" data-testid="share-native"
                onClick={() => {
                  if (navigator.share) navigator.share({ title, url }).catch(() => {});
                  else { navigator.clipboard.writeText(url); toast.success("Link copied"); }
                }}
              >
                <Share2 className="mr-1 h-3.5 w-3.5" /> Share
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---- local favorites ----
const FAV_KEY = "coachi_favorites";
export function getFavorites() {
  try { return JSON.parse(localStorage.getItem(FAV_KEY) || "[]"); } catch { return []; }
}
export function isFavorite(code) { return getFavorites().includes(code); }
export function toggleFavorite(code) {
  const cur = getFavorites();
  const next = cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code];
  localStorage.setItem(FAV_KEY, JSON.stringify(next));
  return next;
}

export function TeamAvatar({ team, size = 36 }) {
  const [failedLogo, setFailedLogo] = useState(null);
  if (team?.logo_url && failedLogo !== team.logo_url) {
    return <img src={fileUrl(team.logo_url)} alt={team.name || "Team logo"}
      className="shrink-0 rounded-lg bg-card object-contain" style={{ width: size, height: size }}
      onError={() => setFailedLogo(team.logo_url)} />;
  }
  const color = team?.color || "#94a3b8";
  const initials = (team?.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <div
      className="flex items-center justify-center rounded-lg font-bold text-white shrink-0"
      style={{ width: size, height: size, backgroundColor: color, fontSize: size * 0.4 }}
    >
      {initials}
    </div>
  );
}

export function fmtTime(iso, timeZone) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone, timeZoneName: "short" } : {}) });
  } catch { return "—"; }
}
export function fmtDateTime(iso, timeZone) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", ...(timeZone ? { timeZone, timeZoneName: "short" } : {}) });
  } catch { return "—"; }
}
